import type { AudioTuning } from './tuning';

/**
 * Pure mixing logic, kept free of browser APIs so it can be unit tested.
 *
 * Every remote audio track is routed to one bus. Buses are faded as a group.
 */
export type Bus =
  /** Other players speaking publicly in the main room. */
  | 'public'
  /** The quizmaster's normal voice. */
  | 'qm'
  /** Sound from the quizmaster's shared screen (music/video rounds). Mixed like public voices. */
  | 'media'
  /** Your teammates whispering (team room only). */
  | 'team'
  /** The quizmaster's priority announcement voice. Always full volume. */
  | 'announce';

export const BUSES: Bus[] = ['public', 'qm', 'media', 'team', 'announce'];

export interface MixInputs {
  /** Someone on your team (you included) whispered within the hold window. */
  teamWhisperActive: boolean;
  /** The quizmaster is announcing. Takes precedence over whisper ducking. */
  announcementActive: boolean;
}

/** PRD §3.7 mixing table. */
export function targetGains(inputs: MixInputs, t: AudioTuning): Record<Bus, number> {
  if (inputs.announcementActive) {
    return {
      public: t.announcePublicGain,
      media: t.announcePublicGain,
      qm: 1,
      team: t.announceTeamGain,
      announce: 1,
    };
  }
  if (inputs.teamWhisperActive) {
    return { public: t.duckedGain, media: t.duckedGain, qm: t.duckedGain, team: 1, announce: 1 };
  }
  return { public: 1, media: 1, qm: 1, team: 1, announce: 1 };
}

/** Getting quieter uses the quick fade; getting louder uses the gentle one. */
export function rampDurationMs(current: number, target: number, t: AudioTuning): number {
  if (Math.abs(current - target) < 0.001) return 0;
  return target < current ? t.fadeDownMs : t.fadeUpMs;
}

/**
 * Voice activity across several sources (your own whisper + each teammate's whisper track).
 * A source counts as speaking once its level stays above the threshold for `vadAttackMs`.
 * The group stays active until nobody has spoken for `holdMs` (PRD §3.5).
 */
export class ActivityDetector {
  private aboveSince = new Map<string, number>();
  private lastSpeech = -Infinity;

  constructor(private tuning: () => AudioTuning) {}

  observe(sourceId: string, levelDb: number, now: number) {
    const t = this.tuning();
    if (levelDb >= t.vadThresholdDb) {
      const since = this.aboveSince.get(sourceId) ?? now;
      this.aboveSince.set(sourceId, since);
      if (now - since >= t.vadAttackMs) this.lastSpeech = now;
    } else {
      this.aboveSince.delete(sourceId);
    }
  }

  forget(sourceId: string) {
    this.aboveSince.delete(sourceId);
  }

  isActive(now: number): boolean {
    return now - this.lastSpeech < this.tuning().holdMs;
  }

  reset() {
    this.aboveSince.clear();
    this.lastSpeech = -Infinity;
  }
}

/** RMS of a block of samples in dBFS (silence → -120). */
export function rmsDb(samples: ArrayLike<number>): number {
  let sum = 0;
  for (let i = 0; i < samples.length; i++) sum += samples[i]! * samples[i]!;
  const mean = samples.length ? sum / samples.length : 0;
  return mean > 1e-12 ? 10 * Math.log10(mean) : -120;
}
